/**
 * Reports Hub — Phase 1
 *
 * Landing page for the Reports section.
 * Shows all available reports as cards with quick-stat previews.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart2, DollarSign, Target, ChevronRight, Loader2, MapPin, Users, Hammer,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

async function getAuthToken() {
  const { getAuth } = await import('firebase/auth');
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

const fmt = (n) =>
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// ── Report card ───────────────────────────────────────────────────────────────

const ReportCard = ({ icon: Icon, iconColor, iconBg, title, description, href, stats, loading }) => (
  <Link
    to={href}
    className="group bg-white rounded-xl border border-gray-200 p-6 shadow-sm
      hover:border-orange-300 hover:shadow-md transition-all duration-200 block"
    data-testid={`report-card-${href.split('/').pop()}`}
  >
    <div className="flex items-start justify-between mb-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-orange-400 transition-colors mt-0.5" />
    </div>
    <h3 className="font-semibold text-gray-900 mb-1.5">{title}</h3>
    <p className="text-sm text-gray-500 leading-relaxed mb-4">{description}</p>

    {loading ? (
      <div className="pt-3 border-t border-gray-100">
        <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
      </div>
    ) : stats && (
      <div className="pt-3 border-t border-gray-100 grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="text-lg font-bold text-gray-900" data-testid={`hub-stat-${s.testKey}`}>
              {s.value}
            </p>
            <p className="text-xs text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>
    )}
  </Link>
);

// ── Coming-soon report card ───────────────────────────────────────────────────

const ComingSoonReport = ({ icon: Icon, iconBg, iconColor, title, description }) => (
  <div className="bg-white rounded-xl border border-dashed border-gray-200 p-6 opacity-60 select-none">
    <div className="flex items-start justify-between mb-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg}`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
        Coming Soon
      </span>
    </div>
    <h3 className="font-semibold text-gray-700 mb-1.5">{title}</h3>
    <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
  </div>
);

// ── Reports Hub ───────────────────────────────────────────────────────────────

const Reports = () => {
  const [revData,  setRevData]  = useState(null);
  const [winData,  setWinData]  = useState(null);
  const [srcData,  setSrcData]  = useState(null);
  const [techData, setTechData] = useState(null);
  const [jobData,  setJobData]  = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [rev, win, src, tech, jobs] = await Promise.all([
          axios.get(`${API}/api/reports/revenue?period=this_month`, { headers }),
          axios.get(`${API}/api/reports/win-rate?period=this_month`, { headers }),
          axios.get(`${API}/api/reports/lead-sources?period=this_month`, { headers }),
          axios.get(`${API}/api/reports/technician?period=this_month`, { headers }),
          axios.get(`${API}/api/reports/job-types?period=this_month`, { headers }),
        ]);
        if (cancelled) return;
        setRevData(rev.data);
        setWinData(win.data);
        setSrcData(src.data);
        setTechData(tech.data);
        setJobData(jobs.data);
      } catch (err) {
        console.error('[Reports hub]', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-12">

      {/* Header */}
      <div className="pt-4">
        <div className="flex items-center gap-3 mb-1">
          <BarChart2 className="w-7 h-7 text-[var(--cb-orange)]" />
          <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
        </div>
        <p className="text-gray-500 ml-10">
          Analyze your business performance — revenue, win rates, and more.
        </p>
      </div>

      {/* Available reports */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Available Now
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReportCard
            icon={DollarSign}
            iconColor="text-green-600"
            iconBg="bg-green-50"
            title="Revenue Dashboard"
            description="Track paid revenue, outstanding invoices, completed jobs, and average job value."
            href="/reports/revenue"
            loading={loading}
            stats={revData ? [
              { label: 'This Month',     value: fmt(revData.revenueThisMonth), testKey: 'revenue' },
              { label: 'Outstanding',    value: fmt(revData.outstandingAmount), testKey: 'outstanding' },
              { label: 'Jobs Completed', value: String(revData.completedJobs),  testKey: 'jobs' },
              { label: 'Avg Job Value',  value: fmt(revData.averageJobValue),   testKey: 'avg-job' },
            ] : null}
          />
          <ReportCard
            icon={Target}
            iconColor="text-orange-500"
            iconBg="bg-orange-50"
            title="Win Rate Analytics"
            description="Understand your quote conversion — how many leads turn into won jobs."
            href="/reports/win-rate"
            loading={loading}
            stats={winData ? [
              { label: 'Quotes Sent',  value: String(winData.totalSent),          testKey: 'sent' },
              { label: 'Won',          value: String(winData.approved),            testKey: 'won' },
              { label: 'Lost',         value: String(winData.rejected),            testKey: 'lost' },
              { label: 'Conversion',   value: `${winData.conversionRate}%`,        testKey: 'conversion' },
            ] : null}
          />
          <ReportCard
            icon={MapPin}
            iconColor="text-purple-500"
            iconBg="bg-purple-50"
            title="Lead Source Performance"
            description="See which channels convert best and deliver the highest value jobs."
            href="/reports/lead-sources"
            loading={loading}
            stats={srcData ? [
              { label: 'Total Leads',   value: String(srcData.totalLeads),           testKey: 'total-leads' },
              { label: 'Converted',     value: String(srcData.convertedLeads),       testKey: 'converted' },
              { label: 'Conv Rate',     value: `${srcData.conversionRate}%`,         testKey: 'conv-rate' },
              { label: 'Revenue',       value: fmt(srcData.revenueFromLeads),        testKey: 'revenue' },
            ] : null}
          />
          <ReportCard
            icon={Users}
            iconColor="text-blue-500"
            iconBg="bg-blue-50"
            title="Technician Performance"
            description="Jobs completed and revenue attributed per team member."
            href="/reports/technician"
            loading={loading}
            stats={techData ? [
              { label: 'Jobs Completed',  value: String(techData.totalJobs),            testKey: 'total-jobs' },
              { label: 'Active Techs',    value: String(techData.activeTechs),          testKey: 'active-techs' },
              { label: 'Avg Jobs/Tech',   value: String(techData.avgJobsPerTech),       testKey: 'avg-jobs' },
              { label: 'Revenue',         value: fmt(techData.revenueAttributed),       testKey: 'revenue-attr' },
            ] : null}
          />
          <ReportCard
            icon={Hammer}
            iconColor="text-orange-500"
            iconBg="bg-orange-50"
            title="Job Types"
            description="See your service mix — which job types you perform most and which generate the most revenue."
            href="/reports/job-types"
            loading={loading}
            stats={jobData ? [
              { label: 'Jobs Completed',  value: String(jobData.totalJobs),            testKey: 'total-jobs' },
              { label: 'Service Types',   value: String(jobData.uniqueServiceTypes),   testKey: 'types' },
              { label: 'Top Type',        value: jobData.topJobType,                   testKey: 'top-type' },
              { label: 'Revenue',         value: fmt(jobData.revenueAttributed),       testKey: 'revenue' },
            ] : null}
          />
        </div>
      </section>

    </div>
  );
};

export default Reports;
